import React, {useState, useEffect, useRef, useContext, Suspense} from 'react';
import NotificationsDevelopment from "./NotificationsDevelopment";
import Bookmarks from "./Bookmarks";
import {
    BookmarksIcon,
    CrossIcon,
    MenuIcon,
    NotificationsIcon,
    SearchIcon
} from "../common/icons";
import SmallScreenSettings from "./SmallScreenSettings";
import {NavigationContext} from "../common/NavigationContext";
import {MoreHoriz} from "@mui/icons-material";
import NavigationRightDevelopment from "./NavigationRightDevelopment";
import CircularLoader from "../common/CircularLoader";
import {UserContext} from "../common/UserContext";
import FocusTrap from "focus-trap-react";
import {useScreenMode} from "../common/context/ScreenModeContext";

const Search = React.lazy(() => import("./Search"));

const NavigationSmallScreenDevelopment = ({ children, closeNav, isSeriesNavVisible }) => {
    const [ selectedComponent, setSelectedComponent ] = useState(null);
    const [ notificationBadgeCount, setNotificationBadgeCount ] = useState(0);
    const { isNavVisible, isNavShowingRight, isDrawerVisible } = useContext(NavigationContext);
    const { user } = useContext(UserContext);
    const tabRefs = useRef([]);
    const closeButtonRef = useRef();

    const handleKeyDown = (e, index) => {
        const numTabs = navItems.length;
        let newIndex = index;

        switch (e.key) {
            case 'ArrowRight':
                newIndex = (index + 1) % numTabs;
                break;
            case 'ArrowLeft':
                newIndex = (index - 1 + numTabs) % numTabs;
                break;
            case 'Home':
                newIndex = 0;
                break;
            case 'End':
                newIndex = numTabs - 1;
                break;
            case 'Tab':
                if (!e.shiftKey && index === numTabs - 1) {
                    e.preventDefault();
                    componentRefs[selectedComponent]?.current?.focus();
                    return;
                }
                if (e.shiftKey && index === 0) {
                    e.preventDefault();
                    closeButtonRef.current?.focus();
                    return;
                }
                return;
            case 'Enter':
            case ' ':
                e.preventDefault();
                navItems[index].onClick();
                return;
            default:
                return;
        }

        e.preventDefault();
        tabRefs.current[newIndex]?.focus();
    };

    const handleContentKeyDown = (e) => {
        if (e.key === 'Tab') {
            if (!e.shiftKey) {
                e.preventDefault();
                closeButtonRef.current?.focus();
            } else {
                e.preventDefault();
                const selectedIndex = navItems.findIndex(item => item.key === selectedComponent);
                tabRefs.current[selectedIndex]?.focus();
            }
        }
    };

    const handleCloseButtonKeyDown = (e) => {
        if (e.key === 'Tab') {
            if (e.shiftKey) {
                e.preventDefault();
                componentRefs[selectedComponent]?.current?.focus();
            } else {
                e.preventDefault();
                tabRefs.current[0]?.focus();
            }
        }
    };

    const componentRefs = {
        PageNavigation: useRef(),
        Notifications: useRef(),
        Bookmarks: useRef(),
        Search: useRef(),
        MoreItems: useRef()
    };

    const navItems = [
        {
            key: 'PageNavigation',
            label: 'PageNavigation',
            onClick: () => setSelectedComponent('PageNavigation'),
            component: <div className={'ch-nav-contextual-body'}>{children}</div>,
            icon: <MenuIcon />
        },
        {
            key: 'Notifications',
            label: 'Notifications',
            onClick: () => setSelectedComponent('Notifications'),
            component: (
                <>
                    <h2>Notifications</h2>
                    <NotificationsDevelopment setBadgeCount={setNotificationBadgeCount} />
                </>
            ),
            icon: <NotificationsIcon />
        },
        {
            key: 'Bookmarks',
            label: 'Bookmarks',
            onClick: () => setSelectedComponent('Bookmarks'),
            component: (
                <>
                    <h2>Bookmarks</h2>
                    <Bookmarks/>
                </>
            ),
            icon: <BookmarksIcon />
        },
        {
            key: 'Search',
            label: 'Search',
            onClick: () => setSelectedComponent('Search'),
            component: null,
            icon: <SearchIcon />
        },
        {
            key: 'MoreItems',
            label: 'More items',
            onClick: () => setSelectedComponent('MoreItems'),
            component: <SmallScreenSettings />,
            icon: <MoreHoriz />
        },
    ];

    const handleMenuClose = () => {
        setSelectedComponent(null);
        closeNav();
    };

    useEffect(() => {
        if (isNavVisible) {
            const defaultTab = !user.expired && (!user.publicSession || user.syllabusGroupId)
                ? 'PageNavigation'
                : 'MoreItems';

            setSelectedComponent(defaultTab);

            setTimeout(() => {
                const defaultIndex = navItems.findIndex((item) => item.key === defaultTab);
                tabRefs.current[defaultIndex]?.focus();
            }, 0);
        }
    }, [isNavVisible]);

    if (isNavShowingRight) {
        return (
            <NavigationRightDevelopment closeNav={closeNav}/>
        )
    } else {
        return (
            <FocusTrap
                active={isNavVisible && !isSeriesNavVisible}
                focusTrapOptions={{
                    tabbableOptions: {
                        displayCheck: 'none'
                    },
                    initialFocus: false,
                    allowOutsideClick: false,
                    escapeDeactivates: false
                }}
            >
                <nav aria-label="Main Navigation" className={`ch-nav-mobile ${isNavVisible ? ' visible' : ''}`}>
                    {(!user.expired && (!user.publicSession || user.syllabusGroupId)) &&
                        <div className={'ch-nav-tab-bar'} role="tablist">
                            {navItems.map((item, index) => (
                                <SmallScreenMenuButton
                                    key={item.key}
                                    ref={(el) => (tabRefs.current[index] = el)}
                                    item={item}
                                    selectedComponent={selectedComponent}
                                    badgeCount={item.label === 'Notifications' ? notificationBadgeCount : null}
                                    ariaLabel={item.label === 'Notifications'
                                        ? `Notifications ${notificationBadgeCount} item`
                                        : item.label}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                />
                            ))}
                            <button
                                ref={closeButtonRef}
                                onClick={handleMenuClose}
                                className={'ch-nav-mobile-button'}
                                aria-label={'Close'}
                                role="button"
                                tabIndex={-1}
                                onKeyDown={handleCloseButtonKeyDown}
                            >
                                <CrossIcon/>
                            </button>
                        </div>
                    }

                    <div className={'ch-nav-mobile-wrapper'}>
                        {navItems.map((item) => (
                            <div
                                key={item.key}
                                id={`panel-${item.key}`}
                                role="tabpanel"
                                aria-labelledby={`tab-${item.key}`}
                                hidden={selectedComponent !== item.key}
                                ref={componentRefs[item.key]}
                                tabIndex={-1}
                                aria-live="polite"
                                onKeyDown={handleContentKeyDown}
                            >
                                {item.component}
                            </div>
                        ))}
                        {selectedComponent === 'Search' && (
                            <Suspense fallback={<CircularLoader />}>
                                <div
                                    id="panel-Search"
                                    role="tabpanel"
                                    aria-labelledby="tab-Search"
                                    hidden={selectedComponent !== 'Search'}
                                    ref={componentRefs['Search']}
                                    tabIndex={-1}
                                    aria-live="polite"
                                    onKeyDown={handleContentKeyDown}
                                >
                                    <Search />
                                </div>
                            </Suspense>
                        )}
                    </div>
                </nav>
            </FocusTrap>
        );
    }
}

export default NavigationSmallScreenDevelopment;

const SmallScreenMenuButton = React.forwardRef(
    ({ item, badgeCount, selectedComponent, ariaLabel, onKeyDown }, ref) => {
        const isSelected = selectedComponent === item.key;

        return (
            <button
                ref={ref}
                id={`tab-${item.key}`}
                onClick={item.onClick}
                className={`ch-nav-mobile-button ${isSelected ? 'selected' : ''}`}
                aria-selected={isSelected}
                aria-controls={`panel-${item.key}`}
                aria-label={ariaLabel}
                role="tab"
                tabIndex={isSelected ? 0 : -1}
                onKeyDown={onKeyDown}
            >
                <div>
                    {item.icon}
                    {!!badgeCount && (
                        <div className={'ch-badge'}>
                            <span>{badgeCount}</span>
                        </div>
                    )}
                </div>
            </button>
        );
    }
);